/**
 * Live bridge: USDC.e (Gnosis) → USDG (Robinhood Chain 4663) via LiFi
 * (routed through Relay; ~3s fills, verified 2026-08-10).
 */
import { GNOSIS_CHAIN, RESERVE_TOKEN, ROBINHOOD_CHAIN, LIFI } from "../config";
import { getLifiQuote, sendLifiTransaction, waitLifiDone, LifiError, type LifiQuote } from "../lifi";
import type { LocalWallet } from "../wallet";
import type { Hex } from "../types";
import type { BridgeAdapter, BridgeQuote, BridgeResult } from "./types";

/** Below ~$0.50 LiFi rejects the transfer outright (verified live). */
const ROUTE_MIN_UNITS = 500_000n;

export class LifiBridge implements BridgeAdapter {
  name = "lifi-relay";
  private pending = new Map<string, LifiQuote>();

  constructor(private wallet: LocalWallet) {}

  async getQuote(inputUnits: bigint): Promise<BridgeQuote> {
    const quote = await getLifiQuote({
      fromChain: GNOSIS_CHAIN.id,
      toChain: ROBINHOOD_CHAIN.id,
      fromToken: RESERVE_TOKEN.address,
      toToken: ROBINHOOD_CHAIN.usdg.address,
      fromAmount: inputUnits,
      fromAddress: this.wallet.address,
      slippage: LIFI.bridgeSlippage,
    });
    const quoteId = `lifi-${Date.now()}-${inputUnits}`;
    this.pending.set(quoteId, quote);
    return {
      inputUnits,
      expectedOutputUnits: quote.toAmount,
      feeUnits: inputUnits > quote.toAmount ? inputUnits - quote.toAmount : 0n,
      minInputUnits: ROUTE_MIN_UNITS,
      estimatedSeconds: 5,
      quoteId,
      expiresAt: Date.now() + 60_000,
    };
  }

  async submit(quote: BridgeQuote): Promise<Hex> {
    const lifi = this.pending.get(quote.quoteId);
    if (!lifi) throw new LifiError("Bridge quote expired — requote needed", "failed");
    this.pending.delete(quote.quoteId);
    return sendLifiTransaction(this.wallet.account, lifi);
  }

  /** Wait for delivery — also the recovery path across app restarts. */
  async waitForCompletion(txHash: Hex): Promise<BridgeResult> {
    const done = await waitLifiDone(txHash, GNOSIS_CHAIN.id, ROBINHOOD_CHAIN.id);
    return { txHash, outputUnits: done.receivedAmount ?? 0n };
  }
}
